// Prefer env-configured API base, fall back to same-origin '/api'
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api';
console.log('[ApiClient] Initialized with API_BASE_URL:', API_BASE_URL);

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    // Don't set Content-Type for FormData - let browser set it with boundary
    const isFormData = options.body instanceof FormData;

    console.log(`[ApiClient] Request: ${options.method || 'GET'} ${url}`);
    if (options.method === 'DELETE') {
      console.log(`[ApiClient] Performing DELETE on ${url}`);
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
        ...(this.token && { Authorization: `Bearer ${this.token}` })
      },
      cache: 'no-store',
      signal: AbortSignal.timeout(60000) // 60 second timeout for production uploads
    };

    try {
      const response = await fetch(url, config);
      console.log(`[ApiClient] Response: ${response.status} ${response.statusText} for ${url}`);

      if (response.status === 401) {
        this.logout();
        window.location.href = '/admin';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || error.message || 'Request failed');
      }

      const text = await response.text();
      if (!text) return null; // Handle 204 or empty success responses

      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('API JSON Parse Error for ' + url, e);
        console.log('Raw Response Body:', text);
        return { message: text }; // Fallback for plain text success
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection');
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Network connection failed - please check if server is running');
      }
      throw error;
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  logout() {
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    this.setToken(data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  }

  async register(userData: any) {
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    this.setToken(data.token);
    return data;
  }

  async forgotPassword(email: string) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async updateProfile(data: any) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // Service management
  async getServices() {
    return this.request('/services/');
  }

  async getService(id: string) {
    return this.request(`/services/${id}`);
  }

  async createService(serviceData: any) {
    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData)
    });
  }

  async updateService(id: string, serviceData: any) {
    return this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData)
    });
  }

  async deleteService(id: string) {
    return this.request(`/services/${id}`, {
      method: 'DELETE'
    });
  }

  async getServiceCategories() {
    return this.request('/services/categories');
  }

  async createServiceCategory(categoryData: any) {
    return this.request('/services/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async updateServiceCategory(id: string, categoryData: any) {
    return this.request(`/services/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
  }

  async deleteServiceCategory(id: string, opts?: { force?: boolean }) {
    const qs = opts?.force ? '?force=true' : '';
    return this.request(`/services/categories/${id}${qs}`, {
      method: 'DELETE'
    });
  }

  // Booking management
  async getAvailableSlots(date: string, serviceId?: string) {
    const qs = serviceId ? `?date=${date}&serviceId=${serviceId}` : `?date=${date}`;
    return this.request(`/bookings/available-slots${qs}`);
  }

  async reserveTimeSlot(reservationData: any) {
    return this.request('/bookings/reserve', {
      method: 'POST',
      body: JSON.stringify(reservationData)
    });
  }

  async confirmReservation(reservationId: string) {
    return this.request(`/bookings/confirm/${reservationId}`, {
      method: 'POST'
    });
  }

  async createBooking(bookingData: any) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData)
    });
  }

  async getUserBookings() {
    return this.request('/bookings/my-bookings');
  }

  async getAllBookings() {
    return this.request('/bookings/all');
  }

  async getBooking(id: string) {
    return this.request(`/bookings/${id}`);
  }

  async updateBookingStatus(id: string, status: string) {
    return this.request(`/bookings/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  async cancelBooking(id: string) {
    return this.request(`/bookings/${id}/cancel`, {
      method: 'POST'
    });
  }

  async deleteBooking(id: string) {
    return this.request(`/bookings/${id}`, {
      method: 'DELETE'
    });
  }



  // Gallery management
  async getGalleryItems() {
    return this.request('/gallery');
  }

  async getGalleryItem(id: string) {
    return this.request(`/gallery/${id}`);
  }

  async createGalleryItem(formData: FormData) {
    console.log('Creating gallery item with FormData:', {
      hasToken: !!this.token,
      formDataEntries: Array.from(formData.entries())
    });

    const response = await fetch(`${API_BASE_URL}/gallery`, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` })
      },
      body: formData
    });

    console.log('Gallery create response status:', response.status);

    if (!response.ok) {
      const text = await response.text();
      console.error('Gallery create failed:', { status: response.status, text });
      if (response.status === 413 || /file too large/i.test(text)) {
        throw new Error('File too large. Max 5MB allowed.');
      }
      throw new Error(`Failed to upload gallery item: ${text}`);
    }

    const result = await response.json();
    console.log('Gallery create successful:', result);
    return result;
  }

  async updateGalleryItem(id: string, formData: FormData) {
    console.log('Updating gallery item with FormData:', {
      id,
      hasToken: !!this.token,
      formDataEntries: Array.from(formData.entries())
    });

    const response = await fetch(`${API_BASE_URL}/gallery/${id}`, {
      method: 'PUT',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` })
      },
      body: formData
    });

    console.log('Gallery update response status:', response.status);

    if (!response.ok) {
      const text = await response.text();
      console.error('Gallery update failed:', { status: response.status, text });
      if (response.status === 413 || /file too large/i.test(text)) {
        throw new Error('File too large. Max 5MB allowed.');
      }
      throw new Error(`Failed to update gallery item: ${text}`);
    }

    const result = await response.json();
    console.log('Gallery update successful:', result);
    return result;
  }

  // Gallery categories
  async getGalleryCategories() {
    return this.request('/gallery/categories');
  }

  async createGalleryCategory(categoryData: any) {
    return this.request('/gallery/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async updateGalleryCategory(id: string, categoryData: any) {
    return this.request(`/gallery/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
  }

  async deleteGalleryCategory(id: string) {
    return this.request(`/gallery/categories/${id}`, {
      method: 'DELETE'
    });
  }

  async deleteGalleryItem(id: string) {
    return this.request(`/gallery/${id}`, {
      method: 'DELETE'
    });
  }

  // User management
  async getUsers() {
    return this.request('/users');
  }

  async updateUserRole(userId: string, role: string) {
    return this.request(`/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });
  }

  async updateUserStatus(userId: string, isActive: boolean) {
    return this.request(`/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive })
    });
  }

  // Admin dashboard
  async getDashboardStats() {
    return this.request('/admin/dashboard');
  }

  async getSettings() {
    return this.request('/admin/settings');
  }

  async updateSettings(settings: any) {
    return this.request('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  async getBookingReports(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/admin/reports/bookings?${params}`);
  }

  // Home Page Settings
  async getHomeSettings() {
    return this.request('/home/settings');
  }

  async updateHomeSettings(settings: any) {
    return this.request('/home/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  // Debug endpoint
  async checkUser() {
    return this.request('/home/check-user');
  }

  // Carousel Management
  async getHeroSlides() {
    return this.request('/home/slides');
  }

  async createHeroSlide(slideData: any) {
    const isFormData = slideData instanceof FormData;
    return this.request('/home/slides', {
      method: 'POST',
      body: isFormData ? slideData : JSON.stringify(slideData)
    });
  }

  async updateHeroSlide(id: string, slideData: any) {
    const isFormData = slideData instanceof FormData;
    return this.request(`/home/slides/${id}`, {
      method: 'PUT',
      body: isFormData ? slideData : JSON.stringify(slideData)
    });
  }

  async deleteHeroSlide(id: string) {
    return this.request(`/home/slides/${id}`, {
      method: 'DELETE'
    });
  }

  // Team Members Management
  async getTeamMembers() {
    return this.request('/home/team');
  }

  async createTeamMember(memberData: any) {
    if (typeof FormData !== 'undefined' && memberData instanceof FormData) {
      const response = await fetch(`${API_BASE_URL}/home/team`, {
        method: 'POST',
        headers: {
          ...(this.token && { Authorization: `Bearer ${this.token}` })
        },
        body: memberData
      });
      if (!response.ok) {
        const text = await response.text();
        console.error('Create team member failed:', response.status, text);
        if (response.status === 413 || /file too large/i.test(text)) {
          throw new Error('File too large. Max 5MB allowed.');
        }
        throw new Error(`Failed to create team member: ${text}`);
      }
      return response.json();
    }
    return this.request('/home/team', {
      method: 'POST',
      body: JSON.stringify(memberData)
    });
  }

  async updateTeamMember(id: string, memberData: any) {
    if (typeof FormData !== 'undefined' && memberData instanceof FormData) {
      const response = await fetch(`${API_BASE_URL}/home/team/${id}`, {
        method: 'PUT',
        headers: {
          ...(this.token && { Authorization: `Bearer ${this.token}` })
        },
        body: memberData
      });
      if (!response.ok) {
        const text = await response.text();
        console.error('Update team member failed:', response.status, text);
        if (response.status === 413 || /file too large/i.test(text)) {
          throw new Error('File too large. Max 5MB allowed.');
        }
        throw new Error(`Failed to update team member: ${text}`);
      }
      return response.json();
    }
    return this.request(`/home/team/${id}`, {
      method: 'PUT',
      body: JSON.stringify(memberData)
    });
  }

  async deleteTeamMember(id: string) {
    return this.request(`/home/team/${id}`, {
      method: 'DELETE'
    });
  }

  // Testimonials Management
  async getTestimonials() {
    return this.request('/home/testimonials');
  }

  async createTestimonial(testimonialData: any) {
    return this.request('/home/testimonials', {
      method: 'POST',
      body: JSON.stringify(testimonialData)
    });
  }

  async updateTestimonial(id: string, testimonialData: any) {
    return this.request(`/home/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(testimonialData)
    });
  }

  async deleteTestimonial(id: string) {
    return this.request(`/home/testimonials/${id}`, {
      method: 'DELETE'
    });
  }

  // Offers Management
  async getOffers() {
    return this.request('/home/offers');
  }

  async createOffer(offerData: any) {
    return this.request('/home/offers', {
      method: 'POST',
      body: JSON.stringify(offerData)
    });
  }

  async updateOffer(id: string, offerData: any) {
    return this.request(`/home/offers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(offerData)
    });
  }

  async deleteOffer(id: string) {
    return this.request(`/home/offers/${id}`, {
      method: 'DELETE'
    });
  }

  // Contact Management
  async getContactInfo() {
    return this.request('/contact/info');
  }

  async updateContactInfo(contactData: any) {
    return this.request('/contact/info', {
      method: 'PUT',
      body: JSON.stringify(contactData)
    });
  }

  async getContactPageSettings() {
    return this.request('/contact/page-settings');
  }

  async updateContactPageSettings(settingsData: any) {
    return this.request('/contact/page-settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  async getContactLocations() {
    return this.request('/contact/locations/all');
  }

  async getActiveContactLocations() {
    return this.request('/contact/locations');
  }

  async createContactLocation(locationData: any) {
    return this.request('/contact/locations', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
  }

  async updateContactLocation(id: string, locationData: any) {
    return this.request(`/contact/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(locationData)
    });
  }

  async deleteContactLocation(id: string) {
    return this.request(`/contact/locations/${id}`, {
      method: 'DELETE'
    });
  }

  async submitContactForm(formData: any) {
    return this.request('/contact/submit', {
      method: 'POST',
      body: JSON.stringify(formData)
    });
  }

  // About Page Management
  async getAboutSettings() {
    return this.request('/about/settings');
  }

  async updateAboutSettings(settingsData: any) {
    return this.request('/about/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  // Services Page Management
  async getServicesPageSettings() {
    return this.request('/pages/services/settings');
  }

  async updateServicesPageSettings(settingsData: any) {
    return this.request('/pages/services/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  // Booking Page Management
  async getBookingPageSettings() {
    return this.request('/pages/booking/settings');
  }

  async updateBookingPageSettings(settingsData: any) {
    return this.request('/pages/booking/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  // Gallery Page Management
  async getGalleryPageSettings() {
    return this.request('/pages/gallery/settings');
  }

  async updateGalleryPageSettings(settingsData: any) {
    return this.request('/pages/gallery/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  // Payment Settings Management
  async getPaymentSettings() {
    return this.request('/pages/payment/settings');
  }

  async updatePaymentSettings(settingsData: any) {
    return this.request('/pages/payment/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  // Image Upload
  async uploadImage(formData: FormData, section = 'home') {
    let endpoint;
    switch (section) {
      case 'about':
        endpoint = '/about/upload/image';
        break;
      case 'contact':
        endpoint = '/contact/upload/image';
        break;
      default:
        endpoint = '/home/upload/image';
    }

    console.log(`Uploading image to ${endpoint}`, {
      hasToken: !!this.token,
      formDataEntries: Array.from(formData.entries())
    });

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` })
      },
      body: formData
    });

    console.log(`Upload response status: ${response.status}`);

    if (!response.ok) {
      const text = await response.text();
      console.error('Upload failed:', { status: response.status, text });
      if (response.status === 413 || /file too large/i.test(text)) {
        throw new Error('File too large. Max 5MB allowed.');
      }
      throw new Error(`Failed to upload image: ${text}`);
    }

    const result = await response.json();
    console.log('Upload successful:', result);
    return result;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
